/**
 * UsersGroupDetailModal - Redesigned User Group Modal
 *
 * Modern, professional edit modal using EditModal with:
 * - Tab-based navigation
 * - Consistent form field styling
 * - Keyboard shortcuts
 * - Unsaved changes detection
 */

import React, { useEffect, useState, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {
	BaseForm,
	SwitchBox,
	TextAreaBox,
	TextBox,
} from '../../../../components/base';
import { EditModal, EditPageSection, EditPageField } from '../../../../components/container';
import { CheckedBoxListRole } from '../../../../components/shared/system';
import { UserGroupModel, df_UserGroupModel, valid_UserGroupModel } from '../../../../context/models/users-group/UserGroupModel';
import { Users, Shield, Info } from 'lucide-react';

type IUsersGroupDetailModalProps = {
	isShow: boolean;
	title: string;
	detailModel?: UserGroupModel;
	isSaving: boolean | false;
	onClose: () => void;
	onSubmit: (data: UserGroupModel) => void;
};

const UsersGroupDetailModal = (props: IUsersGroupDetailModalProps) => {
	const { isShow, title, detailModel, onSubmit, onClose, isSaving } = props;
	const isAddMode = !detailModel;
	const [initialValues, setInitialValues] = useState<UserGroupModel>(detailModel || df_UserGroupModel);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	const {
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<UserGroupModel>({
		resolver: yupResolver(valid_UserGroupModel() as any),
		mode: 'all',
		defaultValues: initialValues,
	});

	const onSubmitHandler = (values: UserGroupModel) => {
		onSubmit(values);
		setHasUnsavedChanges(false);
	};

	useEffect(() => {
		if (initialValues) {
			reset(initialValues);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues, reset]);

	useEffect(() => {
		if (detailModel) {
			setInitialValues(detailModel);
			setHasUnsavedChanges(false);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detailModel]);

	// Helper to update field and mark as unsaved
	const updateField = useCallback((field: keyof UserGroupModel, value: any) => {
		setInitialValues((prev) => ({ ...prev, [field]: value }));
		setHasUnsavedChanges(true);
	}, []);

	// Tab content: Basic Info
	const basicInfoTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Tên tác nhân'
				required
				error={errors.name?.message}
				helpText='Tên nhóm người dùng'
			>
				<TextBox
					name='name'
					error={errors.name?.message}
					onValueChanged={(value) => updateField('name', value)}
					value={initialValues.name}
					hasValid={true}
					placeholder='Nhập tên tác nhân...'
				/>
			</EditPageField>

			<EditPageField
				label='Mô tả'
				error={errors.description?.message}
				helpText='Mô tả ngắn về nhóm người dùng'
			>
				<TextAreaBox
					name='description'
					error={errors.description?.message}
					onValueChanged={(value) => updateField('description', value)}
					value={initialValues.description}
					hasValid={true}
					placeholder='Mô tả...'
				/>
			</EditPageField>

			<EditPageField
				label='Trạng thái'
			>
				<SwitchBox
					name='isActive'
					label='Áp dụng nhóm người dùng này'
					error={errors.isActive?.message}
					onValueChanged={(value) => updateField('isActive', value)}
					value={initialValues.isActive}
					sizeClass='sm'
				/>
			</EditPageField>
		</div>
	);

	// Tab content: Permissions
	const permissionsTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Danh sách quyền'
				error={errors.roleIds?.message}
				helpText='Chọn các quyền được gán cho nhóm này'
			>
				<CheckedBoxListRole
					name='roleIds'
					hasValid={true}
					error={errors.roleIds?.message}
					onSelectionChanged={(value: any[]) => {
						updateField('roleIds', value?.filter((x) => x.length > 0).join(','));
					}}
					selectedValue={initialValues.roleIds?.split(',')?.filter((x) => x.length > 0)}
				/>
			</EditPageField>
		</div>
	);

	const tabs = [
		{
			id: 'basic',
			label: 'Thông tin cơ bản',
			icon: <Users className='h-4 w-4' />,
			content: basicInfoTab,
		},
		{
			id: 'permissions',
			label: 'Phân quyền',
			icon: <Shield className='h-4 w-4' />,
			content: permissionsTab,
		},
	];

	return (
		<EditModal
			isOpen={isShow}
			onClose={onClose}
			title={title}
			description={isAddMode ? 'Tạo mới nhóm người dùng' : 'Chỉnh sửa nhóm người dùng'}
			sizeClass='md'
			tabs={tabs}
			isSaving={isSaving}
			onSave={handleSubmit(onSubmitHandler as any)}
			onCancel={onClose}
			hasUnsavedChanges={hasUnsavedChanges}
			breadcrumbs={[
				{ label: 'Trang chủ', href: '/' },
				{ label: 'Nhóm người dùng', href: '/users-group' },
				{ label: isAddMode ? 'Tạo mới' : detailModel?.name || 'Chỉnh sửa' },
			]}
			status={isAddMode ? 'Tạo mới' : undefined}
			statusVariant='default'
		/>
	);
};

export default UsersGroupDetailModal;
